import { supabase } from "../../lib/supabase";
import type {
  AnalyzeCommercialInvoiceFileInput,
  AnalyzeCommercialInvoiceOptions,
  AnalyzeStoredCommercialInvoiceInput,
  CommercialInvoiceAnalysisResult,
} from "./types";
import type { CommercialInvoiceAnalysisCacheRow } from "./commercialInvoiceAnalysisCache";

interface AnalysisResponse {
  success: boolean;
  data?: CommercialInvoiceAnalysisResult;
  error?: string;
}

/** Load persisted analysis for a booking (if any). */
export const fetchCachedCommercialInvoiceAnalysis = async (
  bookingId: number
): Promise<{
  data: CommercialInvoiceAnalysisCacheRow | null;
  error: string | null;
}> => {
  const { data, error } = await supabase
    .from("booking_commercial_invoice_analyses")
    .select(
      "booking_id, object_path, file_name, likelihood_score, completeness_score, proforma_likelihood, document_type, result, analyzed_at"
    )
    .eq("booking_id", bookingId)
    .maybeSingle();

  if (error) {
    return { data: null, error: error.message };
  }

  return { data: (data as CommercialInvoiceAnalysisCacheRow | null) ?? null, error: null };
};

export const invalidateCommercialInvoiceAnalysisCache = async (
  bookingId: number
): Promise<void> => {
  await supabase
    .from("booking_commercial_invoice_analyses")
    .delete()
    .eq("booking_id", bookingId);
};

const fileToBase64 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const invokeAnalysis = async (
  payload: Record<string, unknown>
): Promise<{ data: CommercialInvoiceAnalysisResult | null; error: string | null }> => {
  const { data, error } = await supabase.functions.invoke("analyze-commercial-invoice", {
    body: payload,
  });

  if (error) {
    return { data: null, error: error.message };
  }

  const result = data as AnalysisResponse;
  if (!result?.success || !result.data) {
    return { data: null, error: result?.error ?? "Analysis failed" };
  }

  return { data: result.data, error: null };
};

/** Analyze a commercial invoice already stored on the booking. */
export const analyzeStoredCommercialInvoice = async (
  input: AnalyzeStoredCommercialInvoiceInput,
  options?: AnalyzeCommercialInvoiceOptions
) =>
  invokeAnalysis({
    bookingId: input.bookingId,
    objectPath: input.objectPath,
    deepCheck: options?.deepCheck ?? false,
    forceRefresh: options?.forceRefresh ?? false,
  });

/** Analyze a local file before or after upload (agent portal, future wizard). */
export const analyzeCommercialInvoiceFile = async (
  input: AnalyzeCommercialInvoiceFileInput,
  options?: AnalyzeCommercialInvoiceOptions
) => {
  const fileBase64 = await fileToBase64(input.file);
  return invokeAnalysis({
    bookingId: input.bookingId,
    fileName: input.file.name,
    fileBase64,
    deepCheck: options?.deepCheck ?? false,
  });
};

/** First path from comma-separated commercial_invoice_file column. */
export const getPrimaryCommercialInvoicePath = (
  commercialInvoiceFile: string | null | undefined
): string | null => {
  if (!commercialInvoiceFile?.trim()) return null;
  return commercialInvoiceFile.split(",")[0]?.trim() || null;
};

const normalizeStoredInvoicePath = (objectPath: string): string =>
  objectPath.trim().replace(/^\/+/, "");

const backgroundAnalysisKeys = new Set<string>();

/**
 * Fire-and-forget automatic + AI deep check after upload.
 * Does not block the caller — results are saved to cache for admin/customer views.
 */
export const enqueueBackgroundCommercialInvoiceAnalysis = (
  bookingId: number,
  objectPath: string
): void => {
  const normalizedPath = normalizeStoredInvoicePath(objectPath);
  if (!Number.isFinite(bookingId) || !normalizedPath) return;

  const key = `${bookingId}:${normalizedPath}`;
  if (backgroundAnalysisKeys.has(key)) return;
  backgroundAnalysisKeys.add(key);

  void (async () => {
    try {
      const { error: heuristicError } = await analyzeStoredCommercialInvoice({
        bookingId,
        objectPath: normalizedPath,
      });
      if (heuristicError) {
        console.warn(
          "[CI background] automatic check failed",
          bookingId,
          heuristicError
        );
        return;
      }

      const { error: deepError, data } = await analyzeStoredCommercialInvoice(
        { bookingId, objectPath: normalizedPath },
        { deepCheck: true }
      );
      if (deepError || !data?.deepCheck?.available) {
        console.warn(
          "[CI background] AI deep check failed",
          bookingId,
          deepError ?? data?.deepCheck?.error
        );
      }
    } catch (err) {
      console.warn("[CI background] unexpected error", bookingId, err);
    } finally {
      backgroundAnalysisKeys.delete(key);
    }
  })();
};

export interface BulkCommercialInvoiceAnalysisItem {
  bookingId: number;
  objectPath: string;
}

export interface BulkCommercialInvoiceAnalysisResult {
  succeeded: number;
  failed: number;
  errors: Array<{ bookingId: number; error: string }>;
}

export type BulkAnalyzeStoredCommercialInvoiceProgress = (
  completed: number,
  total: number,
  bookingId: number
) => void;

export type BulkAnalyzeStoredCommercialInvoiceOptions = {
  deepCheck?: boolean;
  onProgress?: BulkAnalyzeStoredCommercialInvoiceProgress;
};

/** Run CI analysis for multiple stored invoices (sequential). */
export const bulkAnalyzeStoredCommercialInvoices = async (
  items: BulkCommercialInvoiceAnalysisItem[],
  optionsOrProgress?:
    | BulkAnalyzeStoredCommercialInvoiceOptions
    | BulkAnalyzeStoredCommercialInvoiceProgress
): Promise<BulkCommercialInvoiceAnalysisResult> => {
  const options: BulkAnalyzeStoredCommercialInvoiceOptions =
    typeof optionsOrProgress === "function"
      ? { onProgress: optionsOrProgress }
      : optionsOrProgress ?? {};

  const deepCheck = options.deepCheck ?? false;
  const onProgress = options.onProgress;
  const errors: BulkCommercialInvoiceAnalysisResult["errors"] = [];
  let succeeded = 0;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    onProgress?.(i, items.length, item.bookingId);

    const { error, data } = await analyzeStoredCommercialInvoice(
      { bookingId: item.bookingId, objectPath: item.objectPath },
      { deepCheck }
    );

    if (error || (deepCheck && !data?.deepCheck?.available)) {
      errors.push({
        bookingId: item.bookingId,
        error:
          error ??
          data?.deepCheck?.error ??
          (deepCheck ? "AI deep check unavailable" : "Analysis failed"),
      });
    } else {
      succeeded += 1;
    }
  }

  onProgress?.(items.length, items.length, items[items.length - 1]?.bookingId ?? 0);

  return {
    succeeded,
    failed: errors.length,
    errors,
  };
};
